import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Breadcrumbs from "components/Common/Breadcrumb";
import DataTable from "../../components/Common/LocalizedDataTable";
import { ServiceCategoryRequestApi } from "apis/ServiceCategoryRequestApi";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import config from "config/config";
import Swal from "../../helpers/swal_helper";
import { formatNumber } from "../../helpers/language_helper";
function ServiceCategoryRequests(props) {

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");

  const [activeData, setActiveData] = useState({
    page: 1,
    limit: config.LIMIT,
    status: "all",
    search: "",
    totalPage: 0
  });

  const searchRef = useRef(null);

  useEffect(() => {
    getCategoryRequests(activeData);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (searchRef.current) {
      clearTimeout(searchRef.current);
    }

    searchRef.current = setTimeout(() => {
      const updated = { ...activeData, search: value, page: 1 };
      setActiveData(updated);
      getCategoryRequests(updated);
    }, 400);
  };

  const columns = [
    {
      name: props.t("Sr.No"),
      selector: (row) => row.id,
      cell: (row) => formatNumber(row.id),
      sortable: true,
      width: "80px"
    },
    {
      name: props.t("Requested Categories"),
      selector: (row) => row.requested_categories,
      sortable: true,
    },
    {
      name: props.t("Salon"),
      selector: (row) => row.salon,
      sortable: true,
    },
    {
      name: props.t("Admin Name"),
      selector: (row) => row.admin,
      sortable: true,
    },
    {
      name: props.t("Status"),
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        const status = props.t(row.status.charAt(0).toUpperCase() + row.status.slice(1));

        let color = "#f5a306";
        if (row.status === "completed") color = "#28a745";
        if (row.status === "rejected") color = "#dc3545";

        return (
          <span
            style={{
              color,
              fontWeight: "500",
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      name: props.t("Action"),
      selector: (row) => row.action,
    },
  ];

  const getCategoryRequests = (sendData) => {
    setLoading(true);

    ServiceCategoryRequestApi.getAll(sendData)
      .then((res) => {
        if (res.data.status) {
          const { data: list, pagination } = res.data.data;

          let tableData = list.map((v, i) => ({
            id: (sendData.page - 1) * sendData.limit + i + 1,
            requested_categories: v.request_category_names,
            salon: v.salon ? v.salon.name : "N/A",
            admin: v.user ? `${v.user.firstname} ${v.user.lastname}` : "Unknown",
            status: v.status,
            action: (
              <>
                <Link
                  style={{ marginRight: "15px" }}
                  to={`/edit-request-category/${v.id}`}
                >
                  <i className="fas fa-edit" id="edittooltip" />
                </Link>
                <i
                  className="mdi mdi-delete font-size-18"
                  style={{ color: "red", cursor: "pointer" }}
                  onClick={() => onDelete(v.id)}
                />
              </>
            ),
          }));

          setData(tableData);

          setActiveData({
            ...sendData,
            totalPage: pagination.total,
          });

        } else {
          Swal.fire("Error", res.data.message, "error");
        }
        setLoading(false);
      })

      .catch(() => {
        setLoading(false);
        Swal.fire("Error", "Failed to load service category requests", "error");
      });
  };

  const onDelete = (id) => {
    Swal.fire({
      title: props.t("Are you sure?"),
      text: props.t("You won't be able to revert this!"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#f46a6a",
      confirmButtonText: props.t("Yes, delete it!"),
      cancelButtonText: props.t("Cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        ServiceCategoryRequestApi.deleteServiceCategoryRequest(id)
          .then((res) => {
            if (res.data.status) {
              Swal.fire("Deleted!", res.data.message, "success");
              getCategoryRequests(activeData);
            } else {
              Swal.fire("Error!", res.data.message, "error");
            }
          })
          .catch(() => {
            Swal.fire("Error!", "Failed to delete request", "error");
          });
      }
    });
  };

  const handleChange = (page) => {
    const updatedData = { ...activeData, page };
    setActiveData(updatedData);
    getCategoryRequests(updatedData);
  };

  const handleRowChange = (limit) => {
    const updatedData = { ...activeData, limit, page: 1 };
    setActiveData(updatedData);
    getCategoryRequests(updatedData);
  };

  document.title = props.t("Service Category Requests") + " | " + props.t("Salon");

  return (
    <div className="page-content">
      <div className="container-fluid">

        <div className="row mb-3">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder={props.t("Search Category")}
              value={searchText}
              onChange={handleSearchChange}
            />
          </div>

          <div className="col-md-3">
            <select
              className="form-control"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                const updated = {
                  ...activeData,
                  status: e.target.value,
                  page: 1,
                };
                setActiveData(updated);
                getCategoryRequests(updated);
              }}
            >
              <option value="all">{props.t("All")}</option>
              <option value="completed">{props.t("Completed")}</option>
              <option value="pending">{props.t("Pending")}</option>
              <option value="rejected">{props.t("Rejected")}</option>
            </select>
          </div>
        </div>

        <DataTable
          className="table-bordered"
          progressPending={loading}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={activeData.totalPage}
          paginationPerPage={activeData.limit}
          onChangeRowsPerPage={handleRowChange}
          onChangePage={handleChange}
          noHeader
          highlightOnHover
          defaultSortField="id"
        />
      </div>
    </div>
  );
}

ServiceCategoryRequests.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(ServiceCategoryRequests);
